<template>
  <div id="modal__statusDetails">
    <BRow align-h="between">
      <BCol xs="12" sm="4">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
        ></b-pagination>
      </BCol>
      <BCol sm="3">
        <h4>{{ currentStatus }}</h4>
      </BCol>
      <BCol sm="2">
        <button @click.stop="() => exportDataToCSV('status-details-table', 'StatusDetails')" class="export-excel__btn">
          Download Excel <font-awesome-icon icon="file-csv" />
        </button>
      </BCol>
      <BCol sm="1" class="mt-2 text-right">
        <BButton variant="outline-secondary" size="sm" @click="clearAllFilters">Clear Filters</BButton>
      </BCol>
      <BCol xs="6" sm="2" md="1" class="perPageDropdown text-right">
        <BDropdown
          id="per-page-dropdown"
          text="Per Page"
          variant="outline-primary"
        >
          <BDropdownItem id="perPage25" active @click="showPerPage(25)">25</BDropdownItem>
          <BDropdownItem id="perPage50" @click="showPerPage(50)">50</BDropdownItem>
          <BDropdownItem id="perPage100" @click="showPerPage(100)">100</BDropdownItem>
          <BDropdownItem id="perPageAll" @click="showPerPage('All')">All</BDropdownItem>
        </BDropdown>
      </BCol>
    </BRow>
    <BRow v-if="showStatusDetailFilters" align-h="between" class="mb-2 filter-row">
      <BCol xs="12" sm="2">
        <BFormSelect v-model="truckYearFilter" :options="truckYearFilterOptions" />
      </BCol>
      <BCol xs="12" sm="2">
        <BFormSelect v-model="truckMakeFilter" :options="truckMakeFilterOptions" />
      </BCol>
      <BCol xs="12" sm="2">
        <BFormSelect v-model="truckModelFilter" :options="truckModelFilterOptions" />
      </BCol>
      <BCol xs="12" sm="2">
        <BFormSelect v-model="truckColorFilter" :options="truckColorFilterOptions" />
      </BCol>
      <BCol xs="12" sm="2">
        <BFormSelect v-model="subStatusFilter" :options="subStatusFilterOptions" />
      </BCol>
      <BCol xs="12" sm="2">
        <BFormSelect v-model="reservedFilter" :options="reservedFilterOptions" />
      </BCol>
    </BRow>
    <b-table
      responsive
      striped
      bordered
      small
      hover
      show-empty
      :items="filteredStatusDetails"
      :fields="selectedStatusFields"
      :per-page="perPage"
      :current-page="currentPage"
      primary-key="UnitNumber"
      id="status-details-table"
    >
    <!-- Bryce, Jason, Brandon, Tanner, Rod, Jeff -->
      <div slot="table-busy" class="text-center text-danger my-2">
        <BSpinner class="align-middle"></BSpinner>
        <strong> Loading...</strong>
      </div>
      <template v-slot:cell(OverrideLate)="data">
        <span v-if="lateDate(data.item, Date.now())">
          <div v-if="data.item.OverrideLate == 1">
            {{data.item.OverrideReason}}
          </div>
        </span>
      </template>
      <template v-slot:cell(UnitNumber)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ data.item.UnitNumber }}</span>
      </template>
      <template v-slot:cell(VinNumber)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ data.item.VinNumber }}</span>
      </template>
      <template v-slot:cell(Description)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ data.item.Description }}</span>
      </template>
      <template v-slot:cell(TruckStatusDescription)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ data.item.TruckStatusDescription }}</span>
      </template>
      <template v-slot:cell(SubStatus)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ data.item.SubStatus }}</span>
      </template>
      <template v-slot:cell(TruckNotes)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ data.item.TruckNotes }}</span>
      </template>
      <template v-slot:cell(ChangeDate)="data">
        <span :class="data.item.isLate ? 'red' : ''">{{ $moment(data.item.ChangeDate, 'MM/DD/YY') }}</span>
      </template>
      <template v-slot:cell(RentalUse)="data" class="rentalUse" v-if="permissions && (permissions.includes('new-trade-counts') || permissions.includes('lot-and-auction'))">
        <span :class="data.item.isLate ? 'red' : ''">
          <div v-if="data.item.RentalUse" class="margin-right-10">
            <BRow align-h="between">
              <BCol sm="6" class="pad-top-6">
                {{ data.item.RentalUse }}
              </BCol>
              <BCol sm="6" v-if="permissions && permissions.includes('set-rental-use') && rentalUseOverride.includes(getUserInfo.UserID)">
                <BButton @click="setRentalUse(data.item.UnitNumber, '')" variant="danger">Undo</BButton>
              </BCol>
            </BRow>
          </div>
          <div v-else-if="permissions && permissions.includes('set-rental-use') && rentalUseOverride.includes(getUserInfo.UserID)" class="margin-right-10">
            <BRow align-h="between">
              <BCol sm="6">
                <BButton @click="setRentalUse(data.item.UnitNumber, 'Rental')" variant="primary">Rental</BButton>
              </BCol>
              <BCol sm="6">
                <BButton @click="setRentalUse(data.item.UnitNumber, 'Trade')" variant="warning">Trade</BButton>
              </BCol>
            </BRow>
          </div>
          <div v-else>
            <font-awesome-icon
              :icon="['fas', 'exclamation-triangle']"
              :title="'This unit has been in ' + currentStatus + ' status too long.'"
            />
          </div>
        </span>
      </template>
    </b-table>
    <div v-if="stillLoadingStatusDetails == true">
      <div slot="table-busy" class="text-center text-danger my-2">
        <BSpinner class="align-middle"></BSpinner>
        <strong> Loading...</strong>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import constants from '@/assets/constants';
import auth from '../../utils/auth/permissionRules';
// import Vue from 'vue';
// import VTooltip from 'v-tooltip';
import { mapActions, mapGetters } from 'vuex';

// Vue.use(VTooltip)
export default {
  name: 'status-details',
  data() {
    return {
      StatusDetailFields: [
        { key: 'OverrideLate', label: 'Late', sortable: false},
        { key: 'UnitNumber', label: 'Unit', sortable: true },
        { key: 'Description', label: 'Description', sortable: true },
        { key: 'TruckStatusDescription', label: "Status", sortable: false },
        { key: 'SubStatus', sortable: true },
        { key: 'TruckNotes', label: 'Notes', sortable: true },
        { key: 'ChangeDate', label: 'Changed', sortable: true },
        { key: 'VinNumber', label: 'VIN', sortable: true },
      ],
      StatusDetailNewFields: [
        { key: 'UnitNumber', sortable: true },
        { key: 'TruckYear', label: 'Year', sortable: true },
        { key: 'TruckMake', label: 'Make', sortable: true},
        { key: 'TruckModel', label: 'Model', sortable: true },
        { key: 'TruckColor', label: 'Color', sortable: true },
        { key: 'Odometer', sortable: true },
        { key: 'TruckStatusDescription', label: 'Status', sortable: true },
        { key: 'SubStatus', sortable: true },
        { key: 'Reserved', sortable: true },
        { key: 'ReservedFor', sortable: true },
        { key: 'ReservedOn', sortable: true },
        { key: 'Posted', sortable: true },
        { key: 'TruckNotes', sortable: true },
        { key: 'ChangeDate', label: 'Last Changed On', sortable: true },
        { key: 'RentalUse', label: 'Rental Use', sortable: true },
        { key: 'VinNumber', label: 'VIN', sortable: true },
      ],
      StatusDetailNewOnlyFields: [
        { key: 'UnitNumber', sortable: true },
        { key: 'TruckYear', label: 'Year', sortable: true },
        { key: 'TruckMake', label: 'Make', sortable: true},
        { key: 'TruckModel', label: 'Model', sortable: true },
        { key: 'TruckColor', label: 'Color', sortable: true },
        { key: 'Odometer', sortable: true },
        { key: 'TruckStatusDescription', label: 'Status', sortable: true },
        { key: 'SubStatus', sortable: true },
        { key: 'Reserved', sortable: true },
        { key: 'ReservedFor', sortable: true },
        { key: 'ReservedOn', sortable: true },
        { key: 'TruckNotes', sortable: true },
        { key: 'ChangeDate', label: 'Last Changed On', sortable: true },
        { key: 'VinNumber', label: 'VIN', sortable: true },
      ],
      StatusDetailNewExecFields: [
        { key: 'UnitNumber', sortable: true },
        { key: 'TruckYear', label: 'Year', sortable: true },
        { key: 'TruckMake', label: 'Make', sortable: true},
        { key: 'TruckModel', label: 'Model', sortable: true },
        { key: 'TruckColor', label: 'Color', sortable: true },
        { key: 'Odometer', sortable: true },
        { key: 'TruckStatusDescription', label: 'Status', sortable: true },
        { key: 'SubStatus', sortable: true },
        { key: 'Reserved', sortable: true },
        { key: 'ReservedFor', sortable: true },
        { key: 'ReservedOn', sortable: true },
        { key: 'Posted', sortable: true },
        { key: 'TruckNotes', sortable: true },
        { key: 'ChangeDate', label: 'Last Changed On', sortable: true },
        { key: 'RentalUse', label: 'Rental Use', sortable: true, class: 'rentalUse' },
        { key: 'VinNumber', label: 'VIN', sortable: true },
      ],
      statusTable: [],
      statusDetails: [],
      statusDetailsOpen: false,
      stillLoadingStatusDetails: true,
      currentStatus: '',
      currentItem: 'ALL',
      currentAvailability: 'all',
      currentModel: 'all',
      currentPage: 1,
      totalRows: 0,
      perPage: 25,
      timeoutVar: null,
      permissions: [],
      rentalUseOverride: [],
      truckYearFilter: '',
      truckMakeFilter: '',
      truckModelFilter: '',
      truckColorFilter: '',
      subStatusFilter: '',
      reservedFilter: '',
    };
  },
  computed: {
    ...mapGetters(['getModalData', 'getUserInfo']),
    optionValues() {
      const unique = (items = [], key) => {
        const values = [...new Set(
          items
            .map(item => (item && item[key] !== undefined ? item[key] : null))
            .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
            .map(value => String(value).trim())
        )];

        return values.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      };

      const toOptions = values => values.map(value => ({ value, text: value }));
      const reservedValues = unique(this.statusDetails, 'Reserved');
      const reservedOptions = reservedValues.map(value => {
        const normalizedValue = String(value).trim().toLowerCase();
        const isReserved = ['true', '1', 'yes', 'y'].includes(normalizedValue);

        return {
          value: isReserved ? 'Yes' : 'No',
          text: isReserved ? 'Yes' : 'No',
        };
      });

      return {
        truckYear: toOptions(unique(this.statusDetails, 'TruckYear')),
        truckMake: toOptions(unique(this.statusDetails, 'TruckMake')),
        truckModel: toOptions(unique(this.statusDetails, 'TruckModel')),
        truckColor: toOptions(unique(this.statusDetails, 'TruckColor')),
        subStatus: toOptions(unique(this.statusDetails, 'SubStatus')),
        reserved: reservedOptions,
      };
    },
    truckYearFilterOptions() {
      return [{ value: '', text: 'All Years' }, ...this.optionValues.truckYear];
    },
    truckMakeFilterOptions() {
      return [{ value: '', text: 'All Makes' }, ...this.optionValues.truckMake];
    },
    truckModelFilterOptions() {
      return [{ value: '', text: 'All Models' }, ...this.optionValues.truckModel];
    },
    truckColorFilterOptions() {
      return [{ value: '', text: 'All Colors' }, ...this.optionValues.truckColor];
    },
    subStatusFilterOptions() {
      return [{ value: '', text: 'All Sub Statuses' }, ...this.optionValues.subStatus];
    },
    reservedFilterOptions() {
      return [{ value: '', text: 'All Reserved' }, ...this.optionValues.reserved];
    },
    selectedStatusFields() {
      if (
        this.permissions &&
        this.permissions.includes('set-rental-use') &&
        this.rentalUseOverride.includes(this.getUserInfo.UserID) &&
        (this.currentStatus == 'NEW' || this.currentStatus == 'RE-RENT' || this.currentStatus == 'NEW & RE-RENT')
      ) {
        return this.StatusDetailNewExecFields;
      }

      if (
        this.currentStatus == 'NEW' ||
        this.currentStatus == 'RE-RENT' ||
        this.currentStatus == 'NEW & RE-RENT'
      ) {
        return this.permissions && (this.permissions.includes('lot-and-auction') || this.permissions.includes('new-trade-counts'))
          ? this.StatusDetailNewFields
          : this.StatusDetailNewOnlyFields;
      }

      return this.StatusDetailFields;
    },
    showStatusDetailFilters() {
      return this.selectedStatusFields !== this.StatusDetailFields;
    },
    filteredStatusDetails() {
      let filtered = [...this.statusDetails];
      const filters = [
        { key: 'TruckYear', value: this.truckYearFilter },
        { key: 'TruckMake', value: this.truckMakeFilter },
        { key: 'TruckModel', value: this.truckModelFilter },
        { key: 'TruckColor', value: this.truckColorFilter },
        { key: 'SubStatus', value: this.subStatusFilter },
        { key: 'Reserved', value: this.reservedFilter },
      ];

      return filtered.filter(item => filters.every(({ key, value }) => {
        if (!value) return true;
        const actualValue = item[key] == null ? '' : String(item[key]).toLowerCase();
        return actualValue.includes(String(value).trim().toLowerCase());
      }));
    },
  },
  mounted() {
    this.statusSelected(this.getModalData.modalCurrentStatus, this.getModalData.modalCurrentNewItem, this.getModalData.modalCurrentNewAvailability, this.getModalData.modalCurrentNewModel);
  },
  async created() {
    this.permissions = auth.permittedFeatures();
    await this.getVariablePermissions();
  },
  methods: {
    isLate(item, now) {
      if (item.OverrideLate == 1) return false;

      return this.lateDate(item, now);
    },
    lateDate(item, now) {
      const changeDate = new Date(item.ChangeDate).getTime();

      if (
        ['Sold Not Delivered', 'IN TRANSIT', 'IN TRANSIT (SELLER)', 'ON LOT']
          .includes(this.currentStatus)
      ) {
        return changeDate < now - 7 * 86400000;
      } else if (
        ['NEW', 'OFF RENT', 'RE-RENT', 'BODY SHOP']
          .includes(this.currentStatus)
      ) {
        return changeDate < now - 14 * 86400000;
      } else if (this.currentStatus === 'Hold for Sale') {
        return changeDate < now - 21 * 86400000;
      } else if (
        ['At Auction', 'In Maintenance', 'FOR SALE', 'RETAIL']
          .includes(this.currentStatus)
      ) {
        return changeDate < now - 28 * 86400000;
      } else if (this.currentStatus === 'Claims') {
        return changeDate < now - 56 * 86400000;
      }

      return false;
    },
    clearAllFilters() {
      this.truckYearFilter = '';
      this.truckMakeFilter = '';
      this.truckModelFilter = '';
      this.truckColorFilter = '';
      this.subStatusFilter = '';
      this.reservedFilter = '';
      this.currentPage = 1;
    },
    statusSelected(e, item, availability, truckModel) {
      this.currentStatus = e.TruckStatusDescription || e;
      this.currentItem = item;
      this.currentAvailability = availability;
      this.currentModel = truckModel;
      this.truckYearFilter = '';
      this.truckMakeFilter = '';
      this.truckModelFilter = '';
      this.truckColorFilter = '';
      this.subStatusFilter = '';
      this.reservedFilter = '';
      this.stillLoadingStatusDetails = true;
      this.statusDetailsOpen = true;
      this.perPage = 25;
      if (!item) {
        this.currentItem = 'ALL';
      }
      if (!availability) {
        this.currentAvailability = 'all';
      }
      if (!truckModel) {
        this.currentModel = 'all';
      }
      axios
        .get(`${constants.BASE_URL}/api/getStatusDetails/${this.currentStatus}/${this.currentItem}/${this.currentAvailability}/${this.currentModel}`, {
          headers: { Authorization: localStorage.getItem('token') },
        })
        .then(result => {
          // this.statusDetails = result.data;
          this.totalRows = result.data.length;
          const now = Date.now();
          this.statusDetails = result.data.map(item => ({
            ...item,
            isLate: this.isLate(item, now),
          }));
          this.stillLoadingStatusDetails = false;
          this.$root.$emit('rentalTrades');
        }).catch(err => {
          console.log('error: ', err);
        })
    },
    showPerPage(amount) {
      const dropdownContainer = document.getElementById('per-page-dropdown');
      const dropdowns = dropdownContainer.getElementsByClassName('dropdown-item');
      for (let i = 0; i < dropdowns.length; i++) {
        if (dropdowns[i].classList.contains('active')) {
          dropdowns[i].classList.toggle('active');
        }
      }
      if (amount === 'All') {
        this.perPage = this.statusDetails.length;
        document.getElementById('perPageAll').classList.add('active');
      } else if (amount === 100) {
        this.perPage = amount;
        document.getElementById('perPage100').classList.add('active');
      } else if (amount === 50) {
        this.perPage = amount;
        document.getElementById('perPage50').classList.add('active');
      } else if (amount === 25) {
        this.perPage = amount;
        document.getElementById('perPage25').classList.add('active');
      }
    },
    exportDataToCSV() {
      let excelDownloadItems = [];
      for (let i = 0; i < this.statusDetails.length; i++) {
        if (this.currentStatus == 'NEW') {
          excelDownloadItems.push({
            UnitNumber: this.statusDetails[i].UnitNumber,
            Year: this.statusDetails[i].TruckYear,
            Make: this.statusDetails[i].TruckMake,
            Model: this.statusDetails[i].TruckModel,
            Color: this.statusDetails[i].TruckColor,
            Status: this.statusDetails[i].TruckStatusDescription,
            SubStatus: this.statusDetails[i].SubStatus,
            Reserved: this.statusDetails[i].Reserved,
            ReservedFor: this.statusDetails[i].ReservedFor,
            ReservedOn: this.statusDetails[i].ReservedOn,
            TruckNotes: this.statusDetails[i].TruckNotes,
            LastChangedOn: this.statusDetails[i].ChangeDate,
            RentalUse: this.statusDetails[i].RentalUse,
            VIN: this.statusDetails[i].VinNumber,
          })
        } else {
          excelDownloadItems.push({
            UnitNumber: this.statusDetails[i].UnitNumber,
            Year: this.statusDetails[i].TruckYear,
            Make: this.statusDetails[i].TruckMake,
            Model: this.statusDetails[i].TruckModel,
            Color: this.statusDetails[i].TruckColor,
            Status: this.statusDetails[i].TruckStatusDescription,
            SubStatus: this.statusDetails[i].SubStatus,
            Reserved: this.statusDetails[i].Reserved,
            ReservedFor: this.statusDetails[i].ReservedFor,
            ReservedOn: this.statusDetails[i].ReservedOn,
            TruckNotes: this.statusDetails[i].TruckNotes,
            LastChangedOn: this.statusDetails[i].ChangeDate,
            Late: this.statusDetails[i].isLate ? 
                'Yes' : this.statusDetails[i].OverrideReason != null ? 'No, ' + this.statusDetails[i].OverrideReason : 'No',
            VIN: this.statusDetails[i].VinNumber,
          })
        }
      }
      let d = new Date();
      let fileTime = (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
      let fileName = `${this.currentStatus + 'Status' + fileTime}.csv`;
      const items = excelDownloadItems;
      const replacer = (key, value) => value === null ? '' : value // specify how to handle null values here
      const header = Object.keys(items[0]);
      let csv = items.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer).replace(/\\"/g, '"').replace('\n', '\\n')).join(','))
      csv.unshift(header.join(','))
      this.downloadCSV(csv.join('\r\n'), fileName);
    },
    downloadCSV(csv, filename) {
      const csvFile = new Blob([csv], { type: 'text/csv' });
      const downloadLink = document.createElement('a');
      downloadLink.download = filename;
      downloadLink.href = window.URL.createObjectURL(csvFile);
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
    },
    setRentalUse(unit, value) {
      clearTimeout(this.timeoutVar);
      axios
        .post(`${constants.BASE_URL}/api/setRentalUse`, {
          UnitNumber: unit,
          NewValue: value,
          ChangeTypeID: 6,
          ChangeFrom: 'Status Count',
        })
        .then(result => {
          for (let i = 0; i < this.statusDetails.length; i++) {
            if (this.statusDetails[i].UnitNumber == unit) {
              this.statusDetails[i].RentalUse = value;
              // setTimeout doesn't wait if you pass parameters unless you create anonymous function to then run it...
              let that = this;
              this.timeoutVar = setTimeout(function() { that.statusSelected(that.currentStatus, that.currentItem, that.currentAvailability, that.currentModel) }, 30000);
              break;
            }
          }
          // this.statusSelected(this.currentStatus);
        })
        .catch(err => {
          console.log(err);
        });
    },
    async getVariablePermissions() {
      await axios.get(`${constants.BASE_URL}/api/variables/get-variables`, { params: { Group: 'Software' } })
        .then(result => {
          let vars = result.data;
          for (let i = 0; i < vars.length; i++) {
            if (vars[i].VariableName == 'Rental Use Override Permissions') {
              this.rentalUseOverride = vars[i].VariableValue;
            }
          }
        }).catch(err => {
          if (err && err.response && err.response.status === 403) {
            this.showLogOutModal = true;
          } else {
            console.log(err);
          }
        })
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/index';
#modal__statusDetails {
  max-height: 85vh;
  overflow-x: hidden;
  overflow-y: auto;
}
.perPageDropdown {
  margin-right: 100px;
  margin-bottom: 25px;
}
.red {
  color: rgb(180, 0, 0);
  font-weight: bold;
}

.export-excel__btn {
  cursor: pointer;
  padding: 0 10px;
  width: 168px;
  height: 30px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  color: white;
  height: 40px;
  display: flex;
  background-color: rgb(83, 172, 103);
  border: 3px solid rgb(120, 196, 138);
  border-radius: 40px;
  box-shadow: 0px 0px 10px -5px rgba(0, 0, 0, 0.75);
  justify-content: space-between;
  align-items: center;
}

.pad-top-6 {
  padding-top: 6px;
}

.margin-right-10 {
  margin-right: 10px;
}

// @media screen and (min-width: 1400px) {
//   .table-responsive {
//     padding-right: 5px;
//     overflow-x: auto;
//   }
// }

</style>
